/**
 * useConnectResultToast — surfaces the OAuth callback outcome.
 *
 * After consent, the BE redirects the browser back into the app with a
 * `?status=` query param (see integration-svc oauth.controller):
 *   - connected                          → success
 *   - error&reason=<reason>              → failure (user declined / exchange failed)
 *   - organization_selection_required    → multi-org (picker not built yet)
 *
 * We toast once on mount and strip the params so a reload doesn't re-fire.
 */

import { useEffect } from 'react'
import { toast } from '@/components/ui/Toast'

function messageForError(reason: string | null): string {
  switch (reason) {
    case 'access_denied':
      return 'Connection cancelled — access was declined on the provider.'
    case 'missing_code_or_state':
      return 'Connection failed — the provider returned an incomplete response.'
    case null:
    case '':
      return 'Connection failed. Please try again.'
    default:
      return `Connection failed: ${reason}`
  }
}

export function useConnectResultToast(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const status = params.get('status')
    if (!status) return

    switch (status) {
      case 'connected':
        toast.success('Connected — initial sync has started.')
        break
      case 'organization_selection_required':
        toast.info('Multiple organisations were authorised. Review them to finish setup.')
        break
      case 'error':
        toast.error(messageForError(params.get('reason')))
        break
      default:
        break
    }

    // Strip status/reason so a refresh doesn't re-toast.
    params.delete('status')
    params.delete('reason')
    params.delete('connectionId')
    const qs = params.toString()
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
  }, [])
}
